import { ArtifactLightbox } from "@/components/artifacts/ArtifactLightbox.tsx";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { api } from "@/lib/http.ts";
import { isComputerDesktopUrl } from "@/shared/computerDesktop.ts";
import { COMPUTER_PASTE_MAX_CHARS, computerDesktopResp } from "@/types/artifacts.ts";
import { useIsMutating, useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ClipboardPaste, Monitor } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useArtifactViewCtx } from "../ArtifactViewContext.tsx";

/**
 * The computer card in the thread: the "Open computer" button, or why there is
 * none. The desktop opens in an overlay owned by this block, which stays
 * mounted for as long as the card is shown.
 */
export const ComputerDesktopBlock = ({
  openPath,
  closePath,
  pastePath,
}: {
  openPath?: string;
  closePath?: string;
  pastePath?: string;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const { isSharedThread } = useArtifactViewCtx();

  // Revoked from an effect's cleanup rather than from the close button, so
  // every way the link stops being shown ends it: closing the overlay, the card
  // unmounting under an open desktop on navigation or a refetch, and a second
  // link replacing the first. A close handler alone would cover only the first,
  // and the other two would leave a live link nothing in the app is holding.
  useEffect(() => {
    if (url === null) return;
    return () => revokeComputerDesktopLink(closePath, url);
  }, [url, closePath]);

  // A shared thread's viewer is not the account owner and cannot reach this
  // computer, so offering the button would invite a click that must fail.
  if (isSharedThread) {
    return <SharedThreadNotice />;
  }

  // Rendered outside a thread — a notification email — where the route is
  // unbuildable. Say where to go rather than showing a button that cannot work.
  if (!openPath) {
    return <p className="text-xs text-muted-foreground">Open the thread to use the computer.</p>;
  }

  // The button alone. `reason` is already the message this card hangs off, so
  // repeating it here says the same sentence twice a few pixels apart.
  return (
    <div>
      <OpenComputerButton openPath={openPath} onOpened={setUrl} />
      <ComputerDesktopFrame url={url} pastePath={pastePath} onClose={() => setUrl(null)} />
    </div>
  );
};

/**
 * Shown instead of the button to a shared thread's viewer, who is not the
 * account owner and cannot reach the computer. Shared by the card and the
 * version of it above the composer, so both say the same thing.
 */
export const SharedThreadNotice = () => (
  <p className="text-xs text-muted-foreground">Only the account owner can open this.</p>
);

/**
 * Tell the server the desktop was closed, so it revokes the link.
 *
 * The link is a bearer capability with up to half an hour left on it, and a
 * copy that got out while the desktop was open would otherwise stay good after
 * the user has finished. The server holds no copy, so the one this client was
 * given is handed back.
 *
 * Fire and forget: the user is closing a window, the link expires on its own if
 * this never lands, and a server that predates the route names no `closePath`.
 */
export const revokeComputerDesktopLink = (closePath: string | undefined, url: string): void => {
  if (!closePath) return;
  void api.postServerPath(closePath, { url }).catch(() => {});
};

/**
 * The "Open computer" button: mints a desktop link and hands it to `onOpened`.
 *
 * The link is fetched when the button is pressed and never put on the artifact.
 * It is signed and expires after half an hour, so a link carried in the thread
 * payload would be dead long before the card stopped being rendered, and would
 * travel to every surface the card reaches.
 *
 * Opening is slow on purpose-built terms — the computer is stopped far more
 * often than not, and starting it plus booting the desktop takes seconds. That
 * is the normal path, not an error path, so the button says what it is doing
 * rather than appearing to hang.
 */
export const OpenComputerButton = ({
  openPath,
  onOpened,
}: {
  openPath: string;
  onOpened: (url: string) => void;
}) => {
  const open = useMutation({
    mutationFn: async () => {
      const resp = await api.postServerPath<unknown>(openPath);
      // Parsed rather than trusted: this string goes straight into an iframe
      // `src`, so a malformed response should fail here and not in the frame.
      const { url } = computerDesktopResp.parse(resp.data);
      // And only the desktop viewer is framed. The frame sits in our chrome
      // under our title, and the CSP has to allow the provider's whole preview
      // domain, so this is what keeps anything else out of it.
      if (!isComputerDesktopUrl(url)) {
        throw new Error("The desktop link is not one this app will frame.");
      }
      // Handed over here rather than returned. A returned link would sit on the
      // mutation as its result for as long as the button is mounted, making the
      // query cache a second place holding a live capability.
      onOpened(url);
    },
    onError: () => {
      errorToast("Couldn't open the computer. Try again.");
    },
  });

  return (
    <Button
      type="button"
      variant="secondary"
      data-statsig-name="computer-desktop-open"
      disabled={open.isPending}
      onClick={() => open.mutate()}
    >
      <Monitor className="w-4 h-4" />
      {open.isPending ? "Starting computer…" : "Open computer"}
    </Button>
  );
};

/** The user's clipboard could not be read: permission refused, or no API. */
class ClipboardReadError extends Error {}

/** The clipboard holds more than one paste may carry. */
class PasteTooLongError extends Error {}

/**
 * The server refused the paste with a message for the user: the computer has
 * stopped, or is gone. Its messages are fixed strings that never echo the text.
 */
class PasteRefusedError extends Error {}

/**
 * Start reading the clipboard, as a promise that rejects with
 * {@link ClipboardReadError} when it cannot be read.
 *
 * Called from the click itself, not from the mutation: the mutation runs its
 * function a tick later, and Safari only lets a page read the clipboard while it
 * is handling the gesture. `navigator.clipboard` is absent outside a secure
 * context, where calling it throws rather than rejects.
 */
function readClipboard(): Promise<string> {
  try {
    return navigator.clipboard.readText().catch(() => {
      throw new ClipboardReadError();
    });
  } catch {
    return Promise.reject(new ClipboardReadError());
  }
}

/** One key for every Paste button, so a second overlay waits for the first paste. */
const PASTE_MUTATION_KEY = ["computerDesktopPaste"] as const;

/**
 * The header's "Paste" button: reads the user's clipboard and types it on the
 * computer.
 *
 * The frame has no clipboard access — the page in it is served by a computer
 * the agent controls — so this page reads the clipboard when the user asks, and
 * the server types it. Cmd+V inside the frame cannot do this: the browser hands
 * the keypress to the frame, and noVNC sends it on as a key.
 *
 * The text is often a password, so it never leaves the mutation function: the
 * mutation's variables are the pending read rather than the text, it has no
 * result, and a failed request is re-thrown as an error that does not carry it.
 *
 * The desktop keeps the keyboard, so the user can press Enter after pasting a
 * password without that Enter landing here and pasting it again: a click never
 * moves focus to the button, and once a paste settles `onPasted` hands focus
 * back to the desktop, which covers reaching the button with Tab.
 */
const PasteToComputerButton = ({
  pastePath,
  onPasted,
}: {
  pastePath: string;
  onPasted: () => void;
}) => {
  // Disabled while any paste is typing, not just this button's: closing and
  // reopening the overlay mounts a new button, and two pastes at once would
  // interleave their keystrokes on the computer.
  const pasting = useIsMutating({ mutationKey: PASTE_MUTATION_KEY }) > 0;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const paste = useMutation({
    mutationKey: PASTE_MUTATION_KEY,
    mutationFn: async (clipboard: Promise<string>) => {
      // Windows line endings would type each line break as two Enters.
      const text = (await clipboard).replace(/\r\n?/g, "\n");
      if (text.length === 0) return;
      if (text.length > COMPUTER_PASTE_MAX_CHARS) throw new PasteTooLongError();
      try {
        await api.postServerPath(pastePath, { text });
      } catch (error) {
        const refusal = isAxiosError<{ error?: unknown }>(error)
          ? error.response?.data?.error
          : undefined;
        if (isAxiosError(error) && error.response?.status === 409 && typeof refusal === "string") {
          throw new PasteRefusedError(refusal);
        }
        throw new Error("The paste request failed.");
      }
    },
    onError: (error) => {
      if (error instanceof ClipboardReadError) {
        errorToast("Couldn't read your clipboard. Check your browser's clipboard permission.");
      } else if (error instanceof PasteTooLongError) {
        errorToast(
          `That's too long to paste — ${COMPUTER_PASTE_MAX_CHARS.toLocaleString()} characters at most.`,
        );
      } else if (error instanceof PasteRefusedError) {
        errorToast(error.message);
      } else {
        errorToast("Couldn't paste into the computer. Try again.");
      }
    },
    // Only when focus is still where the paste left it: on the button, or on
    // the page once the button was disabled under it. A user who moved to
    // another control while it typed keeps that control.
    onSettled: () => {
      const active = document.activeElement;
      if (active === null || active === document.body || active === buttonRef.current) {
        onPasted();
      }
    },
  });

  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="secondary"
      size="xs"
      data-statsig-name="computer-desktop-paste"
      disabled={pasting}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => paste.mutate(readClipboard())}
    >
      <ClipboardPaste className="w-4 h-4" />
      {pasting ? "Pasting…" : "Paste"}
    </Button>
  );
};

/**
 * The desktop, in an overlay. Open while `url` is set.
 *
 * Its owner holds the link in component state only, so closing the overlay
 * discards it. The owner also asks the server to revoke the link whenever it
 * stops showing it, which `onClose` is one cause of.
 *
 * The Paste button shows only when the card names a `pastePath`, which a server
 * that predates the route does not.
 */
export const ComputerDesktopFrame = ({
  url,
  pastePath,
  onClose,
}: {
  url: string | null;
  pastePath?: string;
  onClose: () => void;
}) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  return (
    <ArtifactLightbox
      open={url !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      icon={Monitor}
      title="My computer"
      hideFooter
      headerActions={
        pastePath ? (
          <PasteToComputerButton pastePath={pastePath} onPasted={() => frameRef.current?.focus()} />
        ) : undefined
      }
    >
      {url === null ? null : (
        <iframe
          ref={frameRef}
          src={url}
          title="My computer"
          // `rr-block` keeps the frame out of session replay. The recorder
          // copies a cross-origin frame's `src` into the recording, and this
          // `src` is the link itself — a live capability on the user's computer
          // that would otherwise be sent to a third party on every recorded
          // session. Blocked elements are recorded as an empty box of the same
          // size, with no attributes.
          className="rr-block w-full h-full min-h-[70vh] border-0 bg-black"
          // The frame serves whatever is listening on port 6080 of the
          // account's own computer, where the agent has root while acting on
          // email and web content it does not control. Being cross-origin
          // already keeps it out of our DOM; what that does not stop is
          // navigating the whole tab or opening windows, so `sandbox` takes
          // both away. `allow-same-origin` keeps the frame on its own origin
          // — which is what lets noVNC hold its WebSocket — and grants it
          // nothing of ours.
          //
          // Nor is it given clipboard access: whatever serves this port could
          // read or overwrite the user's own clipboard with it. Pasting is the
          // header's Paste button, which reads the clipboard on this page.
          sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock"
          referrerPolicy="no-referrer"
        />
      )}
    </ArtifactLightbox>
  );
};
